import React, { useEffect, useContext, useMemo } from "react";
import dayjs from "dayjs";
import { Button, CardNotification, Heading, Text } from "@foris/avocado-suite";
import { BookingContext } from "../../../../context/BookingContext";
import { Types } from "../../../../context/search.reducer";
import DayRow from "../../../../components/DayRow/DayRow";
import Requirements from "../../../../components/Requirements/Requirements";
import Preferences from "../../../../components/Preferences/Preferences";
import useGetOptions from "../../../../hooks/useGetOptions";
import useGetBuilding from "../../../../hooks/useGetBuilding";
import useGetClassroomType from "../../../../hooks/useGetClassroomType";
import useGetEventsCollision from "@modules/booking/hooks/useGetEventsCollision";
import { getHasBlockOptions } from "@modules/booking/utils/blockOptions";
import uuid from "uuid-random";
import { Holiday } from "@models/ISchema";
import * as formats from "@utils/formats";
import { useTranslation } from "react-i18next";
import useGetIntervals from "@modules/booking/hooks/useIntervals";
import useBookingContextUrl from "@modules/booking/hooks/useBookingContextUrl";
import css from "./singleEvent.module.scss";
import searchCss from "../search.module.scss";

interface SingleEventProps {
  selectedHolidays?: Holiday[];
  hasNotUsableDatesSelected?: boolean;
  isScenarioOutsideRange?: boolean;
  onCollission?: (hasCollision: boolean) => void;
  onOutsideRange?: (isOutsideRange: boolean) => void;
}

const SingleEvent: React.FC<SingleEventProps> = ({
  selectedHolidays,
  hasNotUsableDatesSelected,
  onCollission,
  onOutsideRange,
}) => {
  const { state, dispatch } = useContext(BookingContext);
  const { scenario, isAdminBooking } = useBookingContextUrl();
  const [data, getBlocksOptions] = useGetOptions();
  const [building, getBuildingsOptions] = useGetBuilding();
  const [classroomTypes, getClassroomOptions] = useGetClassroomType();
  const hasBlockOptions = getHasBlockOptions(state?.search?.blockOptions);
  const [intervals, getIntervals] = useGetIntervals();
  const { t } = useTranslation();

  useGetEventsCollision({ days: state?.search?.dateTime, onCollission });

  const handleGetClassroomOptions = (buildingId?: string) => {
    if (!classroomTypes?.loading) {
      getClassroomOptions(scenario, buildingId);
    }
  };

  const someDateIsOutsideRange = useMemo(() => {
    const lastIntervalDate =
      state?.search?.intervals?.[state?.search?.intervals?.length - 1]?.endingDate;

    if (!lastIntervalDate) return false;

    const adaptedLastIntervalDate = dayjs(lastIntervalDate).format("YYYY-MM-DD");
    return state?.search?.dateTime?.some(
      day => day.day.value > adaptedLastIntervalDate && !!day.end.value && !!day.start.value,
    );
  }, [state?.search?.dateTime, state?.search?.intervals]);

  useEffect(() => {
    onOutsideRange?.(someDateIsOutsideRange);

    return () => {
      onOutsideRange?.(false);
    };
  }, [someDateIsOutsideRange]);

  useEffect(() => {
    if (state?.search?.intervals === null && !intervals?.data) {
      getIntervals(scenario, undefined, false);
    }

    if (!hasBlockOptions && !data?.data && !data?.loading) {
      getBlocksOptions(scenario);
    }

    if (state?.search?.buildingOptions?.length === 0 && !building?.data && !building?.loading) {
      getBuildingsOptions(scenario);
    }

    if (
      state?.search?.classroomTypesOptions?.length === 0 &&
      !classroomTypes?.data &&
      !classroomTypes?.loading
    ) {
      handleGetClassroomOptions();
    }

    return () => {
      onCollission?.(false);
    };
  }, []);

  return (
    <section className={css.singleEvent}>
      {hasBlockOptions && (
        <>
          <section className={css.singleEvent_dateRange}>
            <Heading type="h4" className={css.dateRangeTitle}>
              {t("booking.booking-form.date-selector.title")}
            </Heading>

            {state?.search?.dateTime?.map((days, idx: number) => (
              <DayRow key={uuid()} index={days?.index ?? idx + 1} day="date" />
            ))}

            <Button
              size="sm"
              variant="ghost"
              leftIcon="circle-plus"
              onClick={() => {
                dispatch({
                  type: Types.SetDateTime,
                  payload: [
                    ...(state?.search?.dateTime ?? []),
                    {
                      index: state?.search?.dateTime?.length + 1,
                      recurrence: false,
                      day: {
                        value: dayjs(new Date()).format("YYYY-MM-DD"),
                        error: false,
                      },
                      start: {
                        value: null,
                        error: false,
                      },
                      end: {
                        value: null,
                        error: false,
                      },
                    },
                  ],
                });
              }}
            >
              {t("booking.booking-form.date-selector.add-day")}
            </Button>

            {!!selectedHolidays?.length && (
              <CardNotification
                className={css.singleEvent_notificationCard}
                title={t("booking.booking-form.notifications.holiday.title", {
                  count: selectedHolidays?.length ?? 0,
                })}
                state={isAdminBooking ? "warning" : "error"}
              >
                <>
                  {selectedHolidays?.map(holiday => (
                    <Text className={searchCss.cardWarning_text} key={holiday?.code} type="sm">
                      {t("booking.booking-form.notifications.holiday.description", {
                        date: formats.formats.dateEsFormat(holiday?.date),
                        name: holiday?.name,
                      })}
                    </Text>
                  ))}
                </>
              </CardNotification>
            )}

            {hasNotUsableDatesSelected && (
              <CardNotification
                className={css.singleEvent_notificationCard}
                title={t("booking.booking-list-app.notifications.not-usable-date.title")}
                state={isAdminBooking ? "warning" : "error"}
              >
                <Text type="sm">
                  {t("booking.booking-list-app.notifications.not-usable-date.description")}
                </Text>
              </CardNotification>
            )}

            {someDateIsOutsideRange && (
              <CardNotification
                className={css.classroomSearch_errorNotification}
                title={t("booking.booking-request.notifications.interval-not-found.title")}
                state="error"
                outlined
              >
                <Text type="sm">
                  {t("booking.booking-request.notifications.interval-not-found.description")}
                </Text>
              </CardNotification>
            )}
          </section>
          <Requirements />
          <Preferences
            isLoadingClassroomTypes={classroomTypes?.loading}
            onBuildingChange={handleGetClassroomOptions}
          />
        </>
      )}
    </section>
  );
};

export default SingleEvent;
